import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Cookie, Settings, BarChart3, Shield, Check } from 'lucide-react';
import { Button } from '../../components/ui/button';

export default function Cookies() {
  const [showBanner, setShowBanner] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always required
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already set preferences
    const saved = localStorage.getItem('cookie_preferences');
    if (!saved) {
      setShowBanner(true);
    } else {
      setPreferences(JSON.parse(saved));
    }
  }, []);

  const savePreferences = (newPrefs) => {
    setPreferences(newPrefs);
    localStorage.setItem('cookie_preferences', JSON.stringify(newPrefs));
    setShowBanner(false);
  };

  const acceptAll = () => {
    savePreferences({ necessary: true, analytics: true, marketing: true });
  };

  const rejectNonEssential = () => {
    savePreferences({ necessary: true, analytics: false, marketing: false });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Cookie Banner */}
      {showBanner && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 shadow-2xl z-50 p-4 sm:p-6" data-testid="cookie-banner">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Cookie className="w-5 h-5 text-indigo-600" />
                <span className="font-semibold text-slate-900">Cookie Settings</span>
              </div>
              <p className="text-sm text-slate-600">
                We use cookies to improve your experience. You can customize your preferences or accept all cookies.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button 
                variant="outline" 
                size="sm"
                onClick={rejectNonEssential}
                data-testid="cookie-reject-btn"
              >
                Essential Only
              </Button>
              <Button 
                size="sm"
                className="bg-gradient-to-r from-indigo-600 to-violet-600 text-white"
                onClick={acceptAll}
                data-testid="cookie-accept-btn"
              >
                Accept All
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/edrano-logo-dark.png" alt="Edrano" className="w-8 h-8 rounded-lg" />
              <span className="font-bold text-lg text-slate-900">Edrano</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="py-16" data-testid="cookies-content">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Cookie Policy</h1>
          <p className="text-slate-500 mb-8">Last updated: February 12, 2026</p>

          {/* Cookie Preferences Card */}
          <div className="p-6 bg-white rounded-2xl border border-slate-200 mb-12">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Settings className="w-5 h-5 text-indigo-600" />
              Your Cookie Preferences
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between py-3 border-b border-slate-100">
                <div>
                  <p className="font-medium text-slate-900">Necessary Cookies</p>
                  <p className="text-sm text-slate-500">Required for the website to function</p>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="text-sm text-slate-500">Always active</span>
                </div>
              </div>
              
              <div className="flex items-center justify-between py-3 border-b border-slate-100">
                <div>
                  <p className="font-medium text-slate-900">Analytics Cookies</p>
                  <p className="text-sm text-slate-500">Help us understand how you use the site</p>
                </div>
                <button
                  onClick={() => savePreferences({ ...preferences, analytics: !preferences.analytics })}
                  className={`w-12 h-6 rounded-full transition-colors ${preferences.analytics ? 'bg-indigo-600' : 'bg-slate-300'}`}
                  data-testid="analytics-toggle"
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${preferences.analytics ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>
              
              <div className="flex items-center justify-between py-3">
                <div>
                  <p className="font-medium text-slate-900">Marketing Cookies</p>
                  <p className="text-sm text-slate-500">Used to show relevant advertisements</p>
                </div>
                <button
                  onClick={() => savePreferences({ ...preferences, marketing: !preferences.marketing })}
                  className={`w-12 h-6 rounded-full transition-colors ${preferences.marketing ? 'bg-indigo-600' : 'bg-slate-300'}`}
                  data-testid="marketing-toggle"
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${preferences.marketing ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>
            </div>
          </div>

          {/* Policy Content */}
          <div className="prose prose-slate max-w-none">
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">What Are Cookies?</h2>
              <p className="text-slate-600 leading-relaxed">
                Cookies are small text files stored on your device when you visit a website. They help websites remember your preferences, keep you logged in, and provide analytics about how you use the site.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">Types of Cookies We Use</h2>
              
              <div className="space-y-6">
                <div className="p-4 bg-green-50 rounded-xl border border-green-100">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-5 h-5 text-green-600" />
                    <h3 className="font-semibold text-slate-900">Necessary Cookies</h3>
                  </div>
                  <p className="text-slate-600 text-sm mb-3">
                    These cookies are essential for the website to function properly. They cannot be disabled.
                  </p>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-green-200">
                        <th className="text-left py-2 text-slate-700">Cookie</th>
                        <th className="text-left py-2 text-slate-700">Purpose</th>
                        <th className="text-left py-2 text-slate-700">Duration</th>
                      </tr>
                    </thead>
                    <tbody className="text-slate-600">
                      <tr className="border-b border-green-100">
                        <td className="py-2">session_id</td>
                        <td className="py-2">Maintains your login session</td>
                        <td className="py-2">Session</td>
                      </tr>
                      <tr className="border-b border-green-100">
                        <td className="py-2">csrf_token</td>
                        <td className="py-2">Security token for form submissions</td>
                        <td className="py-2">Session</td>
                      </tr>
                      <tr>
                        <td className="py-2">cookie_preferences</td>
                        <td className="py-2">Stores your cookie choices</td>
                        <td className="py-2">1 year</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <div className="flex items-center gap-2 mb-2">
                    <BarChart3 className="w-5 h-5 text-blue-600" />
                    <h3 className="font-semibold text-slate-900">Analytics Cookies</h3>
                  </div>
                  <p className="text-slate-600 text-sm mb-3">
                    These cookies help us understand how visitors interact with our website.
                  </p>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-blue-200">
                        <th className="text-left py-2 text-slate-700">Cookie</th>
                        <th className="text-left py-2 text-slate-700">Purpose</th>
                        <th className="text-left py-2 text-slate-700">Duration</th>
                      </tr>
                    </thead>
                    <tbody className="text-slate-600">
                      <tr className="border-b border-blue-100">
                        <td className="py-2">_ga</td>
                        <td className="py-2">Google Analytics - distinguishes users</td>
                        <td className="py-2">2 years</td>
                      </tr>
                      <tr>
                        <td className="py-2">_gid</td>
                        <td className="py-2">Google Analytics - distinguishes users</td>
                        <td className="py-2">24 hours</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">Managing Cookies</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                You can manage your cookie preferences using the toggles at the top of this page. You can also configure your browser to block or delete cookies:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">Google Chrome</a></li>
                <li><a href="https://support.mozilla.org/en-US/kb/cookies" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">Mozilla Firefox</a></li>
                <li><a href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">Safari</a></li>
                <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">Microsoft Edge</a></li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">Changes to This Policy</h2>
              <p className="text-slate-600 leading-relaxed">
                We may update this Cookie Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4">Contact Us</h2>
              <p className="text-slate-600 leading-relaxed">
                If you have questions about our use of cookies, please contact us at{' '}
                <a href="mailto:privacy@edrano.my" className="text-indigo-600 hover:underline">privacy@edrano.my</a>.
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 bg-slate-100 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-slate-500">
              © {new Date().getFullYear()} Edrano. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-sm text-slate-500 hover:text-indigo-600">Privacy</Link>
              <Link to="/terms" className="text-sm text-slate-500 hover:text-indigo-600">Terms</Link>
              <Link to="/gdpr" className="text-sm text-slate-500 hover:text-indigo-600">GDPR</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
